import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Button, BackButton, LoadingButton, Dropdown, useToast } from '../../components/ui';

function ContentGeneratorYouTube() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeVideoInfo, setYoutubeVideoInfo] = useState(null);
  const [youtubeTranscript, setYoutubeTranscript] = useState(null);
  const [isLoadingYoutubeInfo, setIsLoadingYoutubeInfo] = useState(false);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [selectedSubtitleLang, setSelectedSubtitleLang] = useState('zh-TW');
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // 內容設定
  const [pageCount, setPageCount] = useState('5');
  const [outputLanguage, setOutputLanguage] = useState('zh-TW');

  const fetchYoutubeInfo = async (url) => {
    if (!url.trim()) return;
    setIsLoadingYoutubeInfo(true);
    setYoutubeVideoInfo(null);
    setYoutubeTranscript(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/youtube/info?url=${encodeURIComponent(url)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setYoutubeVideoInfo(data.data);

          if (data.data.hasTranscript && data.data.availableLanguages?.length > 0) {
            const preferredLangs = ['zh-TW', 'zh', 'zh-Hant', 'en'];
            let selectedLang = data.data.availableLanguages[0].code;
            for (const pref of preferredLangs) {
              const found = data.data.availableLanguages.find(l => l.code === pref || l.code.startsWith(pref.split('-')[0]));
              if (found) {
                selectedLang = found.code;
                break;
              }
            }
            setSelectedSubtitleLang(selectedLang);
          }
          toast.success('影片資訊載入成功');
        } else {
          toast.error(data.error?.message || '無法載入影片資訊');
        }
      }
    } catch (error) {
      console.error('Failed to fetch YouTube info:', error);
      toast.error('無法載入影片資訊');
    } finally {
      setIsLoadingYoutubeInfo(false);
    }
  };

  const fetchYoutubeTranscript = async () => {
    if (!youtubeVideoInfo?.videoId) return;
    setIsLoadingTranscript(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/youtube/transcript', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: youtubeVideoInfo.videoId,
          lang: selectedSubtitleLang,
          format: 'text'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setYoutubeTranscript(data.data);

          // Create project with transcript
          const projectData = {
            type: 'content-generator',
            source: 'youtube',
            currentStep: 1,
            keywords: [youtubeVideoInfo.title || youtubeUrl],
            topic: youtubeVideoInfo.title || youtubeUrl,
            pageCount: parseInt(pageCount),
            contentType: 'carousel',
            language: outputLanguage,
            preset: 'default',
            generationStatus: 'draft',
            youtubeVideo: {
              videoId: youtubeVideoInfo.videoId,
              title: youtubeVideoInfo.title,
              author: youtubeVideoInfo.author,
              duration: youtubeVideoInfo.duration,
              viewCount: youtubeVideoInfo.viewCount,
              thumbnailUrl: youtubeVideoInfo.thumbnailUrl,
              url: youtubeVideoInfo.url
            },
            youtubeTranscript: {
              language: data.data.language,
              characterCount: data.data.characterCount,
              segmentCount: data.data.segmentCount,
              transcript: data.data.transcript
            }
          };

          const createResponse = await fetch('/api/projects/create', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(projectData)
          });

          const createResult = await createResponse.json();
          if (createResult.success) {
            setCurrentProjectId(createResult.project.id);
            toast.success(`已取得字幕並創建專案 (${data.data.language})`);
          } else {
            toast.warning(`已取得字幕，但創建專案失敗`);
          }
        } else {
          toast.error(data.error?.message || '無法取得字幕');
        }
      }
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
      toast.error('無法取得字幕');
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  const handleContinue = () => {
    if (!youtubeVideoInfo) {
      toast.warning('請先取得影片資訊');
      return;
    }
    if (!youtubeTranscript) {
      toast.warning('請先擷取字幕');
      return;
    }
    if (!currentProjectId) {
      toast.warning('請先擷取字幕以創建專案');
      return;
    }
    navigate(`/templates/content-generator/step2?projectId=${currentProjectId}`);
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Home Button */}
      <Button
        onClick={() => navigate('/home')}
        variant="primary"
        size="md"
        icon="mdi:home"
        className="fixed top-8 left-8 z-[100]"
      >
        Home
      </Button>

      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/templates/content-generator')}
        className="fixed bottom-8 left-8 z-[100]"
      >
        Back
      </BackButton>

      {/* Main Content */}
      <div className="max-w-[980px] w-full bg-white p-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-2xl flex items-center justify-center">
            <Icon icon="mdi:youtube" className="text-4xl text-red-500" />
          </div>
          <h1 className="text-4xl font-bold text-black mb-2">YouTube to Post</h1>
          <p className="text-base text-gray-500">Generate content from YouTube video transcripts</p>
        </div>

        {/* URL Input Section */}
        <div className="max-w-[800px] mx-auto">
          <div className="text-sm font-semibold text-slate-800 mb-4">YouTube Video URL</div>
          <div className="flex gap-3 mb-6">
            <Input
              size="large"
              placeholder="Enter YouTube video URL..."
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              onPressEnter={() => youtubeUrl.trim() && fetchYoutubeInfo(youtubeUrl)}
              disabled={isLoadingYoutubeInfo}
              prefix={<Icon icon="mdi:youtube" className="text-red-600 text-xl" />}
              className="flex-1 rounded-lg"
            />
            <LoadingButton
              onClick={() => fetchYoutubeInfo(youtubeUrl)}
              disabled={!youtubeUrl.trim()}
              loading={isLoadingYoutubeInfo}
              loadingText="Loading..."
              icon="mdi:magnify"
              variant="primary"
              size="lg"
            >
              Fetch
            </LoadingButton>
          </div>

          {/* Video Info */}
          {youtubeVideoInfo && (
            <div className="p-6 bg-gray-50 rounded-xl mb-6">
              <div className="flex gap-6">
                <img
                  src={youtubeVideoInfo.thumbnailUrl}
                  alt={youtubeVideoInfo.title}
                  className="w-[200px] h-28 object-cover rounded-lg shrink-0"
                />
                <div className="flex-1">
                  <div className="text-base font-bold text-slate-800 mb-2 leading-snug">
                    {youtubeVideoInfo.title}
                  </div>
                  <div className="text-xs text-slate-500 mb-3">
                    {youtubeVideoInfo.author} • {Math.floor((youtubeVideoInfo.duration || 0) / 60)}:{String((youtubeVideoInfo.duration || 0) % 60).padStart(2, '0')} • {(youtubeVideoInfo.viewCount || 0).toLocaleString()} views
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    {youtubeVideoInfo.hasTranscript ? (
                      <>
                        <div className="flex items-center gap-2 text-xs text-green-600">
                          <Icon icon="mdi:check-circle" />
                          字幕可用
                        </div>
                        <Dropdown
                          value={selectedSubtitleLang}
                          onChange={(value) => setSelectedSubtitleLang(value)}
                          size="sm"
                          className="min-w-[150px]"
                          options={youtubeVideoInfo.availableLanguages?.map(lang => ({
                            value: lang.code,
                            label: `${lang.name}${lang.isAutoGenerated ? ' (自動)' : ''}`
                          })) || []}
                        />
                        <LoadingButton
                          onClick={fetchYoutubeTranscript}
                          loading={isLoadingTranscript}
                          loadingText="擷取中..."
                          icon="mdi:text-box-outline"
                          variant="accent"
                          size="sm"
                        >
                          擷取字幕
                        </LoadingButton>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-red-500">
                        <Icon icon="mdi:close-circle" />
                        此影片沒有字幕
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Transcript Preview */}
              {youtubeTranscript && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Icon icon="mdi:text-box-check" className="text-green-600" />
                    已擷取字幕 ({youtubeTranscript.language}) - {youtubeTranscript.characterCount?.toLocaleString()} 字
                  </div>
                  <div className="max-h-36 overflow-y-auto p-3 bg-white rounded-lg border border-gray-200 text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">
                    {youtubeTranscript.transcript?.substring(0, 500)}
                    {youtubeTranscript.transcript?.length > 500 && '...'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content Settings */}
          {youtubeTranscript && (
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="text-sm font-semibold text-slate-800 mb-4">內容設定</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Dropdown
                    label="輪播頁數"
                    value={pageCount}
                    onChange={(value) => setPageCount(value)}
                    size="md"
                    fullWidth
                    options={[
                      { value: '3', label: '3 頁' },
                      { value: '5', label: '5 頁' },
                      { value: '10', label: '10 頁' }
                    ]}
                  />
                </div>
                <div>
                  <Dropdown
                    label="輸出語言"
                    value={outputLanguage}
                    onChange={(value) => setOutputLanguage(value)}
                    size="md"
                    fullWidth
                    options={[
                      { value: 'zh-TW', label: '繁體中文' },
                      { value: 'en-US', label: 'English' },
                      { value: 'ja-JP', label: '日本語' }
                    ]}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Continue Button */}
          {youtubeTranscript && (
            <Button
              onClick={handleContinue}
              variant="primary"
              size="lg"
              icon="mdi:arrow-right"
              className="w-full"
            >
              Continue to Design
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContentGeneratorYouTube;
